import { fetchWithAuth, getAuthHeader } from "./http.service";
const ADMIN_API_URL = (process.env.REACT_APP_API_URL || "") + "/api/admin/";
const PACKAGES_API_URL = (process.env.REACT_APP_API_URL || "") + "/api/packages";

export interface AppointmentDTO {
  id: number;
  customerName: string;
  date: string;
  time: string;
  service: string;
  staffName: string;
  duration: number;
  cost: number;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'BLOCKED';
  notes?: string;
  email?: string;
  phone?: string;
}

export interface ServiceItem {
  id: number;
  name: string;
  category: 'Men' | 'Women';
  duration: number;
  price: number;
  description?: string;
  requiresCall?: boolean;
}

interface PackageDTO {
  id: number;
  name: string;
  price: number;
  durationMinutes: number;
  description?: string;
  requiresCall?: boolean;
  gender?: string;
}

class AdminAppointmentsService {
  getAppointments(from: string, to: string): Promise<AppointmentDTO[]> {
    return fetchWithAuth(ADMIN_API_URL + `appointments?from=${from}&to=${to}`, {
      method: "GET",
      headers: getAuthHeader(),
    }).then((response) => response.json());
  }

  getServices(): Promise<ServiceItem[]> {
    return fetchWithAuth(PACKAGES_API_URL, {
      method: "GET",
      headers: getAuthHeader(),
    }).then((response) => {
      return response.json().then((data: PackageDTO[]) => data.map((pkg) => this.mapPackageToService(pkg)));
    });
  }

  createService(service: Omit<ServiceItem, "id">): Promise<ServiceItem> {
    return fetchWithAuth(PACKAGES_API_URL, {
      method: "POST",
      headers: getAuthHeader(),
      body: JSON.stringify(this.mapServiceToPackage(service))
    }).then((response) => {
      return response.json().then((pkg: PackageDTO) => this.mapPackageToService(pkg));
    });
  }

  updateService(id: number, service: Omit<ServiceItem, "id">): Promise<ServiceItem> {
    return fetchWithAuth(`${PACKAGES_API_URL}/${id}`, {
      method: "PUT",
      headers: getAuthHeader(),
      body: JSON.stringify(this.mapServiceToPackage(service))
    }).then((response) => {
      return response.json().then((pkg: PackageDTO) => this.mapPackageToService(pkg));
    });
  }

  deleteService(id: number): Promise<void> {
    return fetchWithAuth(`${PACKAGES_API_URL}/${id}`, {
      method: "DELETE",
      headers: getAuthHeader(),
    }).then(() => {
      return;
    });
  }

  updateStatus(id: number, status: AppointmentDTO["status"]): Promise<void> {
    return fetchWithAuth(ADMIN_API_URL + `appointments/${id}/status`, {
      method: "PUT",
      headers: getAuthHeader(),
      body: JSON.stringify({ status })
    }).then(() => {
      return;
    });
  }

  private mapPackageToService(pkg: PackageDTO): ServiceItem {
    const category = (pkg.gender || "").toUpperCase() === "WOMEN" ? "Women" : "Men";
    return {
      id: pkg.id,
      name: pkg.name,
      price: Number(pkg.price ?? 0),
      duration: Number(pkg.durationMinutes ?? 0),
      description: pkg.description || "",
      requiresCall: Boolean(pkg.requiresCall),
      category
    };
  }

  private mapServiceToPackage(service: Omit<ServiceItem, "id">): Omit<PackageDTO, "id"> {
    return {
      name: service.name,
      price: Number(service.price),
      durationMinutes: Number(service.duration),
      description: service.description || "",
      requiresCall: Boolean(service.requiresCall),
      gender: service.category === "Women" ? "WOMEN" : "MEN"
    };
  }
}

const adminAppointmentsService = new AdminAppointmentsService();
export default adminAppointmentsService;
